/* eslint-disable react-hooks/immutability */
import { type Row, type TableOptions } from '@tanstack/react-table';
import { useCallback } from 'react';

import type { DataTableRowOrderProps } from './row-order-types.js';
import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import type { DataTableRowData } from '../../row-data-types.js';
import type { TDataTableFeatures } from '../types/features.types.js';

/**
 * Checks if the `rowOrdering` prop is a configuration object.
 * @param rowOrdering - The table prop that enables row ordering.
 * @returns `true` if the param is an object, `false` otherwise.
 */
function isRowOrderConfigObject<TData>(
  rowOrdering:
    | boolean
    | {
        enableDragAndDrop?: boolean;
        disableRowDragAndDrop?: (row: TData) => boolean;
      },
): rowOrdering is {
  enableDragAndDrop?: boolean;
  disableRowDragAndDrop?: (row: TData) => boolean;
} {
  return typeof rowOrdering === 'object';
}

/**
 * Configuration hook for the row order in the DataTable.
 * @internal
 */
export function useRowOrder<TData extends DataTableRowData>(
  props: DataTableRowOrderProps<TData>,
  options: TableOptions<TDataTableFeatures, Exclude<TData, unknown>>,
): void {
  const { rowOrdering } = props;

  const disableRowDragAndDropFn =
    typeof rowOrdering === 'object'
      ? rowOrdering.disableRowDragAndDrop
      : undefined;

  const enableRowDragAndDropCallback = useCallback(
    (row: Row<TDataTableFeatures, Exclude<TData, unknown>>) => {
      // If no disable function is provided, enable drag and drop for all rows
      if (!disableRowDragAndDropFn) {
        return true;
      }
      return !disableRowDragAndDropFn(row.original);
    },
    [disableRowDragAndDropFn],
  );

  const [rowOrder, onRowOrderChange] = useControllableState<string[]>({
    value: props.rowOrder,
    defaultValue: props.defaultRowOrder,
    onChange: props.onRowOrderChange,
  });

  options.enableRowOrderDragAndDrop = false;
  if (rowOrdering === false || rowOrdering === undefined) {
    options.enableRowOrdering = false;
    return;
  }

  options.enableRowOrdering = true;
  options.onRowOrderChange = onRowOrderChange;
  options.state = { ...options.state, rowOrder };

  // config object is given
  if (isRowOrderConfigObject(rowOrdering)) {
    if (
      rowOrdering.enableDragAndDrop === true &&
      rowOrdering.disableRowDragAndDrop !== undefined
    ) {
      options.lockDisabledRows = rowOrdering.lockDisabledRows;
      options.enableRowOrderDragAndDrop = enableRowDragAndDropCallback;
      options.autoResetPageIndex = false;
    } else {
      options.enableRowOrderDragAndDrop =
        rowOrdering.enableDragAndDrop === true;
    }
  }
}
